import React from "react";
import { Stack } from "@fluentui/react";
import SummaryTable from "./SummaryTable";
import { useAppContext } from "./AppContext";
import { useSearchParams } from "react-router";
import { useNavigate } from 'react-router';
import ProgressBar from './ProgressBar';

export default function Category() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { appData } = useAppContext();

    const categoryId = searchParams.get("categoryId");
    // Filter product items by product_type (case-insensitive)
    const categoryKey = categoryId?.toLowerCase() ?? '';
    const items = appData.data.reportProductItems.filter(item => (item.product_type || '').toLowerCase() === categoryKey);

    // Group by receipt_id for unique receipts
    const receiptMap = new Map();
    for (const item of items) {
        if (!receiptMap.has(item.receipt_id)) {
            receiptMap.set(item.receipt_id, {
                receipt_id: item.receipt_id,
                merchant_name: item.merchant_name,
                receipt_transaction_date: item.receipt_transaction_date,
                total: 0,
                currency_code: item.currency_code || item.receipt_currency_code || '',
            });
        }
        receiptMap.get(item.receipt_id).total += item.total_price || 0;
    }
    const uniqueReceipts = Array.from(receiptMap.values()).map((r, idx) => ({ id: idx, ...r }));
    const totalAmount = uniqueReceipts.reduce((sum, r) => sum + (r.total || 0), 0);

    // Group by product description for unique product items
    const productMap = new Map();
    for (const item of items) {
        const key = item.description || item.product_identifier || item.item_id;
        if (!productMap.has(key)) {
            productMap.set(key, {
                description: item.description,
                quantity: 0,
                total: 0,
            });
        }
        productMap.get(key).quantity += item.quantity || 1;
        productMap.get(key).total += item.total_price || 0;
    }
    const uniqueProducts = Array.from(productMap.values()).map((p, idx) => ({ id: idx, ...p }));

    if (!appData.dataFetched) {
        return <ProgressBar label="Loading" progress={20} />;
    }

    if (!categoryId || items.length === 0) {
        navigate('/');
        return null;
    }

    return (
        <Stack horizontalAlign="center" style={{ width: '100%', margin: '0 auto' }}>
            <div className="entity-summary">
                <br/>
                <div style={{ background: 'rgba(0,0,0,0.08)', width: '90%', borderRadius: '8px', margin: '0 auto', padding: '18px 0' }}>
                    <div style={{ fontWeight: 700, fontSize: 26, margin: 6, letterSpacing: 1 }}>
                        {categoryId}
                    </div>
                    <br/>
                    <div style={{ marginBottom: 8 }}>
                        <strong>{uniqueReceipts.length} receipts - {totalAmount.toFixed(2)} {uniqueReceipts[0]?.currency_code || ''}</strong>
                    </div>
                </div>
                <div>
                    <br/>
                    <strong style={{ fontSize: 16 }}>Products:</strong>
                    <br/>
                    <Stack horizontalAlign="center" style={{ margin: 'auto', width: '90%' }}>
                        <SummaryTable
                            data={uniqueProducts}
                            columns={[
                                { key: "description", label: "Product", width: "40%", total: false },
                                { key: "quantity", label: "Quantity", width: "30%", total: true },
                                { key: "total", label: "Total", width: "22%", total: true },
                            ]}
                            filterKeys={[]}
                            header={true}
                            total={true}
                        />
                    </Stack>
                    <br/>
                    <strong style={{ fontSize: 16 }}>Receipts:</strong>
                    <br/>
                    <Stack horizontalAlign="center" style={{ margin: 'auto', width: '90%' }}>
                        <SummaryTable
                            data={uniqueReceipts}
                            columns={[
                                {
                                    key: "merchant_name",
                                    label: "Merchant",
                                    width: "40%",
                                    total: false,
                                    render: (_: any, item: any) => (
                                        <span className='table-cell-content' onClick={() => navigate(`/Receipt?receiptId=${item.receipt_id}`)} style={{ cursor: 'pointer' }}>{item.merchant_name}</span>
                                    ),
                                },
                                { key: "receipt_transaction_date", label: "Date", width: "30%", total: false },
                                { key: "total", label: "Total", width: "22%", total: true },
                            ]}
                            filterKeys={[]}
                            header={true}
                            total={true}
                        />
                    </Stack>
                </div>
            </div>
        </Stack>
    );
}